import PropTypes from 'prop-types';
import React, {useMemo, useEffect, useState} from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import CigHeader from '../../components/CigHeader';
import CloudRegionPickerView from './CloudRegionPickerView';
import externalLinkIcon from '../../resources/media/external_link.svg';
import questionMark from '../../resources/media/question-mark.svg';
import { verifyNetAppDomain, serverLog } from '../../common/utils';

/**
 * Location Page
 *
 * @param cloudHomeUrl - NetApp Console URL
 * @param selectedRegion - Object of selected cloud region
 * @param availableRegions - Array of available clouds
 * @param setSelectedCloudRegion - Function to set selected cloud region
 * @param setSelectedLocation - Function to set location where tenant will be created
 * @param helpDocumentUrl - Data Infrastructure Insights help URL
 * @param cloudType - Cloud Type (optional), like aws, azure. This will use to show regions of cloudType only. cloudType must match the 'cloud' properties of location object
 * @returns {JSX.Element}
 */
export function LocationPage({cloudHomeUrl, selectedRegion, availableRegions = [], setSelectedCloudRegion, setTenantCreationLocation, helpDocumentUrl, cloudType}) {
    const [selectedCloudRegionType, setSelectedCloudRegionType] = useState(!_.isEmpty(cloudType) ? cloudType : (_.isEmpty(availableRegions) ? 'aws' :
        _.filter(availableRegions, {defaultLocation: true})[0].cloud));

    const validNetAppConsoleUrl = useMemo(() => {
        if (!verifyNetAppDomain(cloudHomeUrl, true)) {
            window.CIG_LOG.error('LocationPage: Invalid NetApp Console URL: ' + cloudHomeUrl);
            const error = {
                category: 'ERROR',
                logMessage: 'LocationPage: invalid redirect path: ' + window.encodeURI(cloudHomeUrl)
            };
            serverLog(null, error);
            return false;
        }
        return true;
    }, [cloudHomeUrl]);

    const availableCloudTypes = useMemo(() => {
        return  !_.isEmpty(cloudType) ?
            _.uniqBy(_.filter(availableRegions, {'cloud' : cloudType}), 'cloud') :
            _.uniqBy(availableRegions, 'cloud');
    }, [cloudType, availableRegions]);

    useEffect(() => {
        if (_.isEmpty(selectedRegion)) {
            const availCloudTypes = availableCloudTypes;
            if (!_.isEmpty(availCloudTypes) && !_.isEmpty(availableRegions)) {
                const availRegionByCloudType = _.filter(availableRegions, {'cloud': _.isEmpty(selectedCloudRegionType) ? availCloudTypes[0].cloud : selectedCloudRegionType});
                if (!_.isEmpty(availRegionByCloudType) && availRegionByCloudType.length === 1) {
                    setSelectedCloudRegion(availRegionByCloudType[0]);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRegion, availableRegions, setSelectedCloudRegion, availableCloudTypes]);

    const getSelectedRegion = (cloudType, availableRegionsByCloudType) => {
        return (_.isEmpty(selectedRegion) && !_.isEmpty(availableRegionsByCloudType) && availableRegionsByCloudType.length === 1) ?
            availableRegionsByCloudType[0] :
            selectedRegion;
    }

    if (_.isEmpty(availableCloudTypes)) {
        window.CIG_LOG.info('Cloud provider not found: ' + cloudType);
    }

    if (_.isEmpty(availableRegions)) {
        window.CIG_LOG.info('There is no available cloud regions');
        return null;
    }

    return (
        <div id="region-setting"  className="tw-font-body" data-testid="region-setting">
            <CigHeader showLogout={true}/>
            <div className="tw-mt-2px">
                <div className="tw-m-auto tw-w-1/2 tw-w-max-content tw-w-min-fix-content tw-pt-xl">
                    <div id="choose-region-label" className="tw-mx-auto tw-my-8px tw-pt-38px tw-pb-12px">
                        <span id="number-one-text" className="tw-mb-0 tw-font-semibold tw-text-lg tw-leading-7 tw-text-black tw-whitespace-no-wrap">
                            Please choose one (1) cloud provider and region to host your Data Infrastructure Insights tenant.
                        </span>
                    </div>
                    {
                        _.map(availableCloudTypes, (item, index) => {
                            if (!_.isEmpty(cloudType) && item.cloud !== cloudType) {
                                return null;
                            }

                            const availableRegionsByCloudType = _.filter(availableRegions, {'cloud' : item.cloud});
                            return (
                                <div className="tw-ml-18px tw-mr-12px" key={`cloud-picker-container-${item.cloud}`} id={`cloud-picker-container-${item.cloud}`}>
                                    <CloudRegionPickerView
                                        id={`cloud-picker-view-${item.cloud}-${index}`}
                                        key={`cloud-picker-view-${item.cloud}`}
                                        data-testid={`cloud-picker-view-${item.cloud}`}
                                        cloudId={item.cloud}
                                        selectedCloudType={selectedCloudRegionType}
                                        selectedItem={getSelectedRegion(item.cloud, availableRegionsByCloudType)}
                                        availableItems={availableRegionsByCloudType}
                                        setSelectedCloudType={setSelectedCloudRegionType}
                                        setSelected={setSelectedCloudRegion}
                                        showRadioButton={availableCloudTypes.length > 1}
                                    />
                                </div>
                            );
                        })
                    }
                    <div className={classnames('tw-pt-56px', {'tw-ml-18px': availableCloudTypes.length > 1})}>
                        <span id="ci-document" className="tw-mt-12px tw-float-left">
                            <a id="ci-document-link" className='tw-text-blue-300 tw-font-title hover:tw-cursor-pointer' href={helpDocumentUrl} rel="noreferrer noopener"  target="_blank">
                                <img className="tw-inline tw-items-center tw-pb-2px" src={questionMark} width="18px" alt=">" />
                                <span className="tw-pr-5px tw-pl-5px tw-text-med">About Regional Support</span>
                                <img className="tw-inline tw-items-center tw-pb-5px" src={externalLinkIcon} width="18px" alt=">" />
                            </a>
                        </span>
                        <span className="tw-float-right">
                            <button id="create-tenant-button"
                                className={classnames('tw-text-med tw-border tw-py-6px tw-px-20px',
                                    {'tw-text-white tw-border-netapp-blue tw-bg-netapp-blue hover:tw-bg-blue-500': !_.isEmpty(selectedRegion)},
                                    {'tw-bg-gray-200 tw-text-gray-500': _.isEmpty(selectedRegion)})}
                                disabled={_.isEmpty(selectedRegion)}
                                onClick={_.partial(setTenantCreationLocation, selectedRegion)}>
                                Create
                            </button>
                        </span>
                        <span id="return-cloud-central" className={classnames('tw-ml-8px tw-border tw-border-gray-300 tw-py-5px tw-px-8px tw-float-right  tw-mb-10px tw-mr-10px',
                            {'pointer-events-none opacity-50 cursor-not-allowed select-none': !validNetAppConsoleUrl, 'hover:tw-bg-gray-250': validNetAppConsoleUrl})}>
                            <a id="ci-cloud-central-link" className={classnames('tw-text-blue-300 tw-font-title tw-text-med hover:tw-no-underline',
                                {'aria-disabled hover:tw-cursor-default': !validNetAppConsoleUrl, 'hover:tw-cursor-pointer': validNetAppConsoleUrl})} href={validNetAppConsoleUrl ? cloudHomeUrl : undefined} rel="noreferrer noopener" target="_self">
                                Return to NetApp Console
                            </a>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

LocationPage.displayName = 'LocationPage';
LocationPage.propTypes = {
    cloudHomeUrl: PropTypes.string,
    selectedRegion: PropTypes.object,
    cloudType: PropTypes.string,
    helpDocumentUrl: PropTypes.string.isRequired,
    availableRegions: PropTypes.array.isRequired,
    setTenantCreationLocation: PropTypes.func.isRequired,
    setSelectedCloudRegion: PropTypes.func.isRequired,
};

export default LocationPage;
